/* 
 * DESIGN: "Leśna Przystań" — Organic Brutalism
 * Home page: hero, USP bar, features, routes preview, dog section, campfire, reviews, form CTA
 */
import { Link } from "wouter";
import { useEffect, useRef } from "react";
import {
  ChevronDown, MapPin, Clock, Users, Shield, Star,
  Dog, Flame, Truck, Award, ArrowRight, Phone, CheckCircle2
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import ReviewsSection from "@/components/ReviewsSection";
import { useAuth } from "@/_core/hooks/useAuth";
import PageMeta from "@/components/PageMeta";
import JsonLd from "@/components/JsonLd";
import SeoBlock from "@/components/SeoBlock";

const HERO_IMG = "/manus-storage/hero_river_1920x1080_q30_95bd3bfa.webp";
const DOG_IMG = "/manus-storage/cover_kayaks_0d037cbb.webp";
const CAMPFIRE_IMG = "/manus-storage/campfire_bg_q55_88e90ed2.webp";
const KAYAK_GROUP_IMG = "/manus-storage/cover_kayaks_0d037cbb.webp";
const FB_KAYAKS_IMG = "/manus-storage/cover_kayaks_0d037cbb.webp";
const RIVER_IMG = "/manus-storage/hero_river_1920x1080_q30_95bd3bfa.webp";

const routes = [
  {
    name: "Spała → Zakościele",
    distance: "10 km",
    time: "2–3 godz.",
    level: "Łatwy",
    levelColor: "oklch(0.55 0.15 155)",
    desc: "Klasyczny spływ przez serce Pilicy. Idealna trasa dla rodzin i początkujących kajakarzy.",
    highlight: "Najpopularniejsza",
    price: "od 80 zł",
  },
  {
    name: "Zakościele → Gapinin",
    distance: "ok. 15 km",
    time: "3–4 godz.",
    level: "Łatwy",
    levelColor: "oklch(0.55 0.15 155)",
    desc: "Spokojna trasa rodzinna wzdłuż Pilicy z pięknymi plażami do postoju.",
    highlight: "Rodzinna",
    price: "od 130 zł",
  },
  {
    name: "Zakościele → Domaniewice",
    distance: "22 km",
    time: "5–6 godz.",
    level: "Średnio zaawansowany",
    levelColor: "oklch(0.62 0.18 45)",
    desc: "Dłuższa wyprawa dla ambitnych. Piękne odcinki dzikiej rzeki, postoje na plażach.",
    highlight: "Wyprawa",
    price: "od 130 zł",
  },
  {
    name: "Zakościele → Nowe Miasto",
    distance: "30 km",
    time: "7–8 godz.",
    level: "Zaawansowany",
    levelColor: "oklch(0.55 0.18 25)",
    desc: "Najdłuższa trasa w ofercie. Pełnodniowa wyprawa przez dzikie tereny Pilicy.",
    highlight: "Full-day",
    price: "od 180 zł",
  },
  {
    name: "Tomaszów Maz. → Zakościele",
    distance: "19–21 km",
    time: "4–5 godz.",
    level: "Przyjemna trasa",
    levelColor: "oklch(0.50 0.15 160)",
    desc: "Przyjemna trasa przebiegająca pod słynnym mostem żelaznym, na którym kręcono serial \"Czterej pancerni i pies\".",
    highlight: "Filmowa",
    price: "od 100 zł",
  },
];

const features = [
  {
    icon: <Shield size={28} />,
    title: "Gwarancja najniższej ceny",
    desc: "Znajdziesz taniej u konkurencji? Damy Ci 5% rabatu. Bez pytań.",
    color: "oklch(0.50 0.18 45)",
  },
  {
    icon: <Dog size={28} />,
    title: "Kajaki przyjazne psom",
    desc: "Twój pies jedzie razem z Tobą. Mamy kapoki dla psów i doświadczenie w psich przygodach.",
    color: "oklch(0.32 0.08 155)",
  },
  {
    icon: <Flame size={28} />,
    title: "Ognisko po spływie — gratis",
    desc: "Drewno i miejsce na ognisko w cenie każdego spływu. Idealne zakończenie dnia na rzece.",
    color: "oklch(0.50 0.18 45)",
  },
  {
    icon: <Award size={28} />,
    title: "Nowy, zadbany sprzęt",
    desc: "Kajaki 1-, 2- i 3-osobowe, wiosła i kapoki — wszystko nowe i regularnie serwisowane. Twoje bezpieczeństwo to priorytet.",
    color: "oklch(0.32 0.08 155)",
  },
  {
    icon: <Truck size={28} />,
    title: "Transport na start i metę — bezpłatny",
    desc: "Transport uczestników na start trasy i odbiór z mety jest wliczony w cenę spływu. Darmowy parking przy bazie. Podwózka z dalszych obszarów (np. spoza Tomaszowa) — wycena indywidualna.",
    color: "oklch(0.50 0.18 45)",
  },
  {
    icon: <Users size={28} />,
    title: "Grupy i eventy",
    desc: "Integracje firmowe, wieczory panieńskie, urodziny. Obsługujemy grupy od 2 do 100+ osób.",
    color: "oklch(0.32 0.08 155)",
  },
];

export default function Home() {
  // The userAuth hooks provides authentication state
  // To implement login/logout functionality, simply call logout() or redirect to getLoginUrl()
  let { user, loading, error, isAuthenticated, logout } = useAuth();

  const revealRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = revealRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    el.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={revealRef} className="min-h-screen" style={{ background: "oklch(0.97 0.02 90)" }}>
      <PageMeta
        title="Kajaki Pilica – Najtańsze Spływy Kajakowe nad Pilicą | Kudłaty Cooky"
        description="Kajaki nad Pilicą w Zakościelu (k. Spały i Inowłodza). Najtańsze spływy kajakowe Pilicą z gwarancją ceny, kajaki z psem, ognisko gratis i transport z Tomaszowa Maz. Rezerwuj online!"
        canonical="/"
      />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {"@type": "Question", "name": "Ile kosztuje spływ kajakowy Pilicą?", "acceptedAnswer": {"@type": "Answer", "text": "Ceny zaczynają się od 100 zł za kajak na popularnej trasie Spała–Zakościele. W cenie są kamizelki, wiosła, transport i darmowe ognisko po spływie. Gwarantujemy najniższą cenę w okolicy."}},
          {"@type": "Question", "name": "Czy trzeba umieć pływać, żeby popłynąć kajakiem?", "acceptedAnswer": {"@type": "Answer", "text": "Nie jest to wymagane — każdy uczestnik dostaje kamizelkę asekuracyjną, a Pilica to spokojna, bezpieczna rzeka idealna dla początkujących i rodzin z dziećmi."}},
          {"@type": "Question", "name": "Czy zapewniacie transport na spływ?", "acceptedAnswer": {"@type": "Answer", "text": "Tak. Transport uczestników na start trasy i odbiór z mety jest bezpłatny — wliczony w cenę spływu. Podwózka z dalszych obszarów (spoza Tomaszowa Mazowieckiego) jest wyceniana indywidualnie. Zadzwoń pod 787 028 770 po szczegóły."}},
          {"@type": "Question", "name": "Czy mogę zabrać psa na spływ?", "acceptedAnswer": {"@type": "Answer", "text": "Tak, pies jest u nas mile widziany. Mamy kapoki (kamizelki) dla psów i polecamy spokojne trasy. Więcej na podstronie Z psem na kajak."}}
        ]
      }} />
      <Navbar />

      {/* ===== HERO SECTION ===== */}
      <section
        className="relative min-h-[92vh] flex items-center overflow-hidden"
        style={{ background: "oklch(0.15 0.04 155)" }}
      >
        {/* Background image — img tag for LCP discoverability + fetchpriority */}
        <img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ filter: "brightness(0.45)" }}
        />
        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, oklch(0.15 0.06 155 / 0.7) 0%, transparent 60%, oklch(0.15 0.04 155 / 0.4) 100%)",
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          <div className="max-w-2xl flex-1">
            {/* Tag */}
            <div
              className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-white/20 backdrop-blur-sm"
              style={{ background: "oklch(0.62 0.18 45 / 0.2)" }}
            >
              <MapPin size={14} className="text-[oklch(0.78_0.12_70)]" />
              <span
                className="text-xs text-white/90"
                style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", textTransform: "uppercase" }}
              >
                Zakościele · Pilica · Polska
              </span>
            </div>

            {/* Main heading */}
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Pilica,{" "}
              <em className="italic" style={{ color: "oklch(0.78 0.12 70)" }}>kajak</em>
              <br />
              i Twój pies.
            </h1>

            <p
              className="text-xl text-white/80 mb-8 leading-relaxed max-w-xl"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Najtańsze spływy kajakowe nad Pilicą z gwarancją ceny. Nowy sprzęt, kajaki przyjazne psom i ognisko na metę.
            </p>

            {/* Price guarantee badge */}
            <div
              className="inline-flex items-center gap-3 mb-8 px-4 py-3 rounded-lg border-2"
              style={{
                background: "oklch(0.62 0.18 45 / 0.15)",
                borderColor: "oklch(0.62 0.18 45)",
              }}
            >
              <Shield size={20} className="text-[oklch(0.78_0.12_70)] flex-shrink-0" />
              <div>
                <div
                  className="text-white font-bold text-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Gwarancja najniższej ceny
                </div>
                <div
                  className="text-white/70 text-xs"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Znajdziesz taniej? Damy Ci jeszcze 5% taniej!
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link href="/rezerwacja" className="btn-ember text-base px-8 py-4 animate-pulse-ember">
                Zarezerwuj spływ
                <ArrowRight size={18} />
              </Link>
              <Link href="/cennik" className="btn-outline-cream text-base px-8 py-4">
                Zobacz cennik
              </Link>
            </div>

            {/* Campfire FREE badge */}
            <Link
              href="/ognisko"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full font-bold text-sm transition-transform hover:scale-105 mt-4"
              style={{
                background: "oklch(0.50 0.18 45)",
                color: "white",
                fontFamily: "'Outfit', sans-serif",
                boxShadow: "0 0 20px oklch(0.50 0.18 45 / 0.5)",
              }}
            >
              <span className="text-lg">🔥</span>
              <span>OGNISKO PO SPŁYWIE — ZAWSZE GRATIS</span>
              <ArrowRight size={14} />
            </Link>

            {/* Quick stats */}
            <div className="flex flex-wrap gap-6 mt-10">
              {[
                { value: "5.0★", label: "Google Reviews" },
                { value: "125+", label: "Opinii klientów" },
                { value: "Nowy", label: "Sprzęt 2025" },
                { value: "7", label: "Trasy" },
                { value: "100%", label: "Psy mile widziane" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div
                    className="text-2xl font-bold text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-xs text-white/60"
                    style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Logo po prawej stronie */}
          <div className="hidden lg:flex flex-shrink-0 items-center justify-center">
            <div
              className="relative"
              style={{
                width: "380px",
                height: "380px",
                borderRadius: "50%",
                background: "oklch(0.97 0.02 90)",
                border: "5px solid oklch(0.78 0.12 70)",
                boxShadow: "0 0 80px oklch(0.62 0.18 45 / 0.5), 0 20px 60px oklch(0 0 0 / 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="/manus-storage/logo_hero_transparent_06600b43.webp"
                alt="Kajaki Kudłaty Cooky"
                loading="eager"
                decoding="async"
                style={{
                  width: "310px",
                  height: "310px",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>

          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 animate-bounce">
          <ChevronDown size={32} />
        </div>

        {/* Diagonal cut */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80L1440 20V80H0Z" fill="oklch(0.97 0.02 90)" />
          </svg>
        </div>
      </section>

      {/* ===== PRICE GUARANTEE SECTION ===== */}
      <section className="py-16 px-4" style={{ background: "oklch(0.97 0.02 90)" }}>
        <div className="max-w-5xl mx-auto">
          <div
            className="rounded-2xl overflow-hidden shadow-xl"
            style={{ background: "oklch(0.22 0.06 155)" }}
          >
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12">
                <div className="section-tag mb-4" style={{ color: "oklch(0.78 0.12 70)" }}>
                  <span>Nasz główny wyróżnik</span>
                </div>
                <h2
                  className="text-3xl md:text-4xl font-bold text-white mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Najtańsze ceny<br />
                  <em className="italic" style={{ color: "oklch(0.78 0.12 70)" }}>gwarantowane</em>
                </h2>
                <p
                  className="text-white/70 mb-6 leading-relaxed"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Przeglądamy oferty konkurencji regularnie i gwarantujemy, że nasze ceny są najniższe w okolicy. Ale to nie wszystko — jeśli znajdziesz gdzieś taniej, zrobimy Ci jeszcze <strong className="text-white">5% rabatu</strong>. Bez pytań, bez komplikacji.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Trasy od 80 zł za kajak",
                    "Kapok i wiosło w cenie",
                    "Transport na start trasy — bezpłatny w cenie",
                    "Odbiór z mety po spływie — bezpłatny w cenie",
                    "Darmowy parking przy bazie",
                    "Podwózka z dalszych obszarów — wycena indywidualna",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white/80" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      <CheckCircle2 size={18} className="text-[oklch(0.50_0.18_45)] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link href="/cennik" className="btn-ember">
                  Zobacz pełny cennik
                  <ArrowRight size={16} />
                </Link>
              </div>
              <div
                className="relative hidden md:block"
                style={{ background: "oklch(0.18 0.06 155)" }}
              >
                <img
                  src={FB_KAYAKS_IMG}
                  alt="Kolorowe kajaki Kudłaty Cooky nad Pilicą"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="text-center p-8 rounded-xl"
                    style={{ background: "oklch(0.22 0.06 155 / 0.85)" }}
                  >
                    <div
                      className="text-7xl font-bold mb-2"
                      style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.50 0.18 45)" }}
                    >
                      -5%
                    </div>
                    <div
                      className="text-white font-semibold text-lg"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      Gwarancja ceny
                    </div>
                    <div
                      className="text-white/60 text-sm mt-1"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      Znajdziesz taniej? Damy Ci 5% taniej!
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES / USP GRID ===== */}
      <section className="py-20 px-4" style={{ background: "oklch(0.97 0.02 90)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 reveal">
            <div className="section-tag justify-center mb-3">
              <span>Dlaczego my?</span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
            >
              Co nas <em className="italic" style={{ color: "oklch(0.50 0.18 45)" }}>wyróżnia</em>
            </h2>
            <p
              className="text-gray-600 max-w-2xl mx-auto"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Nie jesteśmy kolejną wypożyczalnią kajaków. Jesteśmy miejscem, gdzie rzeka, pies i ognisko tworzą wspomnienia na całe życie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={feature.title}
                className="reveal card-hover bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-white"
                  style={{ background: feature.color }}
                >
                  {feature.icon}
                </div>
                <h3
                  className="text-xl font-bold mb-2"
                  style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
                >
                  {feature.title}
                </h3>
                <p
                  className="text-gray-600 text-sm leading-relaxed"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ROUTES SECTION ===== */}
      <section
        className="py-20 px-4"
        style={{ background: "oklch(0.93 0.03 85)" }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 reveal">
            <div className="section-tag justify-center mb-3">
              <span>Trasy spływów</span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
            >
              Wybierz swoją <em className="italic" style={{ color: "oklch(0.50 0.18 45)" }}>trasę</em>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
              6 tras na Pilicy dla każdego poziomu zaawansowania — od 10 do 30 km.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {routes.map((route, i) => (
              <div
                key={route.name}
                className="reveal card-hover bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className="h-2"
                  style={{ background: route.levelColor }}
                />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3
                      className="text-xl font-bold"
                      style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
                    >
                      {route.name}
                    </h3>
                    <span
                      className="text-xs px-2 py-1 rounded-full text-white flex-shrink-0 ml-2"
                      style={{ background: route.levelColor, fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {route.highlight}
                    </span>
                  </div>
                  <p
                    className="text-gray-600 text-sm mb-4 leading-relaxed"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {route.desc}
                  </p>
                  <div className="flex gap-4 text-sm text-gray-500 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <div className="flex items-center gap-1.5">
                      <MapPin size={14} style={{ color: route.levelColor }} />
                      {route.distance}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} style={{ color: route.levelColor }} />
                      {route.time}
                    </div>
                  </div>
                  <Link
                    href="/splyw"
                    className="flex items-center gap-2 text-sm font-semibold transition-colors"
                    style={{ color: route.levelColor, fontFamily: "'Outfit', sans-serif" }}
                  >
                    Szczegóły trasy
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 reveal">
            <Link href="/splyw" className="btn-forest">
              Wszystkie trasy i szczegóły
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== DOG SECTION ===== */}
      <section className="py-20 px-4 overflow-hidden" style={{ background: "oklch(0.97 0.02 90)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <div className="section-tag mb-4">
                <span>Kajaki przyjazne psom</span>
              </div>
              <h2
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
              >
                Twój pies też<br />
                <em className="italic" style={{ color: "oklch(0.50 0.18 45)" }}>zasługuje</em><br />
                na przygodę
              </h2>
              <p
                className="text-gray-600 mb-6 leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Jesteśmy jedną z niewielu wypożyczalni w okolicy, która z radością przyjmuje czworonożnych towarzyszy. Mamy kapoki dla psów, wiemy jak pomóc Twojemu psu wsiąść do kajaka i zadbamy o jego bezpieczeństwo przez cały spływ.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Kapoki dla psów wszystkich rozmiarów",
                  "Doświadczenie z psami na wodzie",
                  "Bezpieczne wejście i wyjście z kajaka",
                  "Postoje na brzegu dla psa",
                  "Pies płynie razem z opiekunem",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <span className="text-[oklch(0.50_0.18_45)] text-lg">🐾</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link href="/z-psem" className="btn-ember">
                Dowiedz się więcej
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="reveal" style={{ transitionDelay: "200ms" }}>
              <div className="relative">
                <img
                  src={DOG_IMG}
                  alt="Pies w kajaku na rzece Pilica"
                  loading="lazy"
                  decoding="async"
                  className="rounded-2xl shadow-2xl w-full object-cover"
                  width={665}
                  height={499}
                  style={{ maxHeight: "500px" }}
                />
                <div
                  className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl shadow-lg"
                  style={{ background: "oklch(0.22 0.06 155)" }}
                >
                  <div
                    className="text-white font-bold text-sm"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    🐕 Psy mile widziane!
                  </div>
                  <div
                    className="text-white/60 text-xs mt-0.5"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    Kapoki dla psów w cenie
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CAMPFIRE SECTION ===== */}
      <section
        className="relative py-24 px-4 overflow-hidden"
        style={{ background: "oklch(0.22 0.06 155)" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(/manus-storage/campfire_bg_q55_88e90ed2.webp)`,
            filter: "brightness(0.35)",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <div className="section-tag mb-4" style={{ color: "oklch(0.78 0.12 70)" }}>
                <span>After spływ</span>
              </div>
              <div
                className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full font-bold text-sm"
                style={{ background: "oklch(0.50 0.18 45)", color: "white", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.05em" }}
              >
                <Flame size={16} />
                BEZPŁATNE OGNISKO PO KAŻDYM SPŁYWIE
              </div>
              <h2
                className="text-4xl md:text-5xl font-bold text-white mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Ognisko w cenie<br />
                <em className="italic" style={{ color: "oklch(0.78 0.12 70)" }}>każdego spływu</em>
              </h2>
              <p
                className="text-white/70 mb-8 leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Spływ to dopiero połowa zabawy. Po powrocie na brzeg czeka na Ciebie <strong className="text-white">bezpłatne ognisko z drewnem</strong> — w cenie każdego spływu. To właśnie tu rodzą się najlepsze wspomnienia.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: "🔥", label: "Ognisko" },
                  { icon: "🪟", label: "Drewno gratis" },
                  { icon: "🎵", label: "Muzyka" },
                  { icon: "🍺", label: "Dobre towarzystwo" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg"
                    style={{ background: "oklch(1 0 0 / 0.1)" }}
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span
                      className="text-white font-medium text-sm"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
              <Link href="/ognisko" className="btn-ember">
                Zaplanuj wieczór
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="reveal" style={{ transitionDelay: "200ms" }}>
              <div
                className="rounded-2xl p-8"
                style={{ background: "oklch(1 0 0 / 0.08)", backdropFilter: "blur(10px)", border: "1px solid oklch(1 0 0 / 0.15)" }}
              >
                <h3
                  className="text-2xl font-bold text-white mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Pakiet "Spływ + Ognisko"
                </h3>
                <ul className="space-y-4">
                  {[
                    "Spływ wybraną trasą",
                    "Kapoki i wiosła w cenie",
                    "Transport kajaków na start — gratis",
                    "Ognisko z drewnem — gratis",
                    "Muzyka z głośnika",
                    "Czas na brzegu do 22:00",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white/80" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      <CheckCircle2 size={18} className="text-[oklch(0.50_0.18_45)] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <Link href="/rezerwacja" className="btn-ember w-full justify-center">
                    Zarezerwuj pakiet
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== GROUPS TEASER ===== */}
      <section className="py-20 px-4" style={{ background: "oklch(0.97 0.02 90)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center mb-12">
            <div className="section-tag justify-center mb-3">
              <span>Dla grup</span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
            >
              Organizujesz <em className="italic" style={{ color: "oklch(0.50 0.18 45)" }}>event</em>?
            </h2>
            <p
              className="text-gray-600 max-w-2xl mx-auto"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Integracja firmowa, wieczór panieński, urodziny, wycieczka szkolna? Obsługujemy grupy od 2 do 100+ osób. Każda grupa jest inna — dlatego każdą traktujemy indywidualnie.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            {[
              { emoji: "👔", label: "Integracja firmowa", href: "/grupy-i-eventy#firmowe" },
              { emoji: "💍", label: "Wieczór panieński", href: "/grupy-i-eventy#panienski" },
              { emoji: "🎂", label: "Urodziny", href: "/grupy-i-eventy#urodziny" },
              { emoji: "🎓", label: "Wycieczka szkolna", href: "/grupy-i-eventy#szkola" },
              { emoji: "👨‍👩‍👧", label: "Rodzinny wyjazd", href: "/grupy-i-eventy#rodzina" },
            ].map((item, i) => (
              <Link
                key={item.label}
                href={item.href}
                className="reveal card-hover bg-white rounded-xl p-5 text-center border border-gray-100 shadow-sm"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="text-4xl mb-3">{item.emoji}</div>
                <div
                  className="text-sm font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif", color: "oklch(0.22 0.06 155)" }}
                >
                  {item.label}
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center reveal">
            <Link href="/grupy-i-eventy" className="btn-forest">
              Sprawdź ofertę dla grup
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WYSPA TUWIMA / LOKALIZACJA ===== */}
      <section className="py-20 px-4" style={{ background: "oklch(0.97 0.02 90)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="reveal grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="section-tag mb-4">
                <span>Nasza lokalizacja</span>
              </div>
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
              >
                Zakościele — miejsce<br />
                <em className="italic" style={{ color: "oklch(0.50 0.18 45)" }}>z duszą i historią</em>
              </h2>
              <p
                className="text-gray-600 mb-4 leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Nasza przystania mieści się w Zakościelu nad Pilicą — miejscowości z wyjątkową historią. Tuż przy naszej bazie leży <strong>Wyspa Tuwima</strong> — nazwana na cześć Juliana Tuwima, jednego z najwybitniejszych polskich poetów.
              </p>
              <p
                className="text-gray-600 mb-4 leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Tuwim spędzał wakacje w Inowłodzu od 1907 do 1914 roku. Pisał później: <em>„W tym pięknym letnisku nad Pilicą pokochałem krajobraz i przyrodę polską, której żadne cuda zagraniczne nie dorównują. Bywałem we Francji, Brazylii, Stanach Zjednoczonych — a wszędzie tęskniłem za Inowłodzem.‟</em>
              </p>
              <p
                className="text-gray-600 leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Płynąc naszymi trasami, mijasz miejsca, które inspirowały wielką polską poezję. To nie tylko spływ — to podróż przez historię.
              </p>
            </div>
            <div className="reveal" style={{ transitionDelay: "200ms" }}>
              <div
                className="rounded-2xl p-8 text-center"
                style={{ background: "oklch(0.22 0.06 155)" }}
              >
                <div className="text-6xl mb-4">📜</div>
                <blockquote
                  className="text-white/90 text-lg italic mb-4 leading-relaxed"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  „Takie letnisko. Jedno słowo: Wypisz — wymaluj — Soplicowo.‟
                </blockquote>
                <div
                  className="text-[oklch(0.78_0.12_70)] text-sm font-semibold"
                  style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em" }}
                >
                  Julian Tuwim o Inowłodzu
                </div>
                <div
                  className="text-white/50 text-xs mt-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  z poematu „Kwiaty Polskie‟
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEDEKA SECTION ===== */}
      <section
        className="py-12 px-4"
        style={{ background: "oklch(0.93 0.03 85)" }}
      >
        <div className="max-w-4xl mx-auto">
          <div
            className="reveal rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6"
            style={{ background: "white", border: "1px solid oklch(0.88 0.04 85)" }}
          >
            <div className="text-5xl flex-shrink-0">❤️</div>
            <div>
              <div
                className="text-xs font-semibold mb-1"
                style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", textTransform: "uppercase", color: "oklch(0.50 0.18 45)" }}
              >
                Wspieramy
              </div>
              <h3
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
              >
                Fundacja Sedeka "Zdążyć z pomocą"
              </h3>
              <p
                className="text-gray-600 leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Kajaki Kudłaty Cooky wspierają Fundację Sedeka "Zdążyć z pomocą". Część naszych przychodów przekazujemy na działalność fundacji, która pomaga potrzebującym. Wybierając nas, pomagasz razem z nami.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== REVIEWS ===== */}
      <ReviewsSection />

      {/* ===== TRANSPORT SECTION ===== */}
      <section className="py-16 px-4" style={{ background: "oklch(0.97 0.02 90)" }}>
        <div className="max-w-5xl mx-auto">
          <div
            className="reveal rounded-2xl overflow-hidden shadow-lg grid md:grid-cols-2"
            style={{ background: "oklch(0.22 0.06 155)" }}
          >
            <div className="p-8 md:p-10">
              <div className="section-tag mb-4" style={{ color: "oklch(0.78 0.12 70)" }}>
                <span>Transport i dojazd</span>
              </div>
              <h2
                className="text-3xl font-bold text-white mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Transport i dojazd<br />
                <em className="italic" style={{ color: "oklch(0.78 0.12 70)" }}>bez stresu</em>
              </h2>
              <p
                className="text-white/70 mb-6 leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Transport uczestników na <strong className="text-white">start trasy i odbiór z mety</strong> jest <strong className="text-white">bezpłatny — wliczony w cenę</strong> spływu. Przy naszej bazie czeka <strong className="text-white">darmowy parking</strong>. Potrzebujesz podwózki z dalszego obszaru (np. spoza Tomaszowa Maz.)? Zadzwoń — wycenimy indywidualnie.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Transport na start trasy — bezpłatny w cenie",
                  "Odbiór z mety po spływie — bezpłatny w cenie",
                  "Darmowy parking przy bazie",
                  "Podwózka z dalszych obszarów — wycena indywidualna",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white/80" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <Truck size={16} className="text-[oklch(0.50_0.18_45)] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a href="tel:+48787028770" className="btn-ember">
                <Phone size={16} />
                Zadzwoń i umów transport
              </a>
            </div>
            <div className="hidden md:block relative">
              <img
                src="/manus-storage/transport_kayaks_09258531.webp"
                alt="Transport kajaków z Tomaszowa Mazowieckiego"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                style={{ minHeight: "300px", maxHeight: "400px" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section
        className="relative py-24 px-4 overflow-hidden"
        style={{ background: "oklch(0.22 0.06 155)" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${RIVER_IMG})` }}
        />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="reveal">
            <h2
              className="text-4xl md:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Gotowy na<br />
              <em className="italic" style={{ color: "oklch(0.78 0.12 70)" }}>przygodę?</em>
            </h2>
            <p
              className="text-white/70 text-xl mb-10"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Zarezerwuj spływ już dziś. Nowy sprzęt, najtańsze ceny, psy mile widziane.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link href="/rezerwacja" className="btn-ember text-lg px-10 py-4">
                Zarezerwuj teraz
                <ArrowRight size={20} />
              </Link>
              <a href="tel:+48787028770" className="btn-outline-cream text-lg px-10 py-4">
                <Phone size={20} />
                787 028 770
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* ===== LOCATIONS SECTION ===== */}
      <section className="py-16 px-4" style={{ background: "oklch(0.93 0.03 85)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 reveal">
            <div className="section-tag justify-center mb-3">
              <span>Lokalizacje</span>
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.22 0.06 155)" }}
            >
              Kajaki w <em className="italic" style={{ color: "oklch(0.62 0.18 45)" }}>Twojej okolicy</em>
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Organizujemy spływy kajakowe Pilicą dla uczestników z całego regionu. Wybierz swoje miasto i sprawdź szczegóły.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { label: "Kajaki Spała", slug: "/kajaki-spala", emoji: "🏕️" },
              { label: "Kajaki Inowłódz", slug: "/kajaki-inowlodz", emoji: "🎯" },
              { label: "Kajaki Tomaszów Maz.", slug: "/kajaki-tomaszow-mazowiecki", emoji: "📍" },
              { label: "Kajaki Smardzewice", slug: "/kajaki-smardzewice", emoji: "🌲" },
              { label: "Kajaki Zakościele", slug: "/kajaki-zakosciele", emoji: "⛵" },
              { label: "Kajaki Gapinin", slug: "/kajaki-gapinin", emoji: "🌿" },
              { label: "Kajaki Domaniewice", slug: "/kajaki-domaniewice", emoji: "⛴️" },
              { label: "Kajaki Nowe Miasto", slug: "/kajaki-nowe-miasto", emoji: "🗺️" },
              { label: "Kajaki Mysiakowiec", slug: "/kajaki-mysiakowiec", emoji: "🎉" },
            ].map((loc, i) => (
              <a
                key={loc.slug}
                href={loc.slug}
                className="reveal flex flex-col items-center gap-2 p-4 rounded-xl bg-white border border-gray-100 shadow-sm card-hover text-center"
                style={{ transitionDelay: `${i * 50}ms`, textDecoration: "none" }}
              >
                <span className="text-2xl">{loc.emoji}</span>
                <span
                  className="text-sm font-semibold leading-tight"
                  style={{ fontFamily: "'Outfit', sans-serif", color: "oklch(0.22 0.06 155)" }}
                >
                  {loc.label}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>
      <SeoBlock
        heading="Spływy kajakowe Pilicą — Zakościele, Spała, Inowłódz, Tomaszów Mazowiecki"
        paragraphs={[
          "Kajaki Kudłaty Cooky to rodzinna wypożyczalnia kajaków nad Pilicą z bazą w Zakościelu (gmina Inowłódz), w sercu Doliny Pilicy. Organizujemy spływy kajakowe Pilicą na trasach od ok. 8 do 30 km — od krótkiej, rodzinnej trasy Spała–Zakościele, po całodniowe wyprawy w stronę Gapinina, Domaniewic i Nowego Miasta nad Pilicą. Niezależnie od tego, czy planujesz pierwszy spływ kajakowy, rodzinny wypad, czy integrację dla firmy — dobierzemy trasę do Twojego poziomu i czasu.",
          "Stawiamy na nowy, zadbany sprzęt, bezpieczeństwo (kapoki dla dorosłych, dzieci i psów) oraz najniższe ceny w okolicy z gwarancją ceny. Po każdym spływie czeka darmowe ognisko z drewnem — idealne zakończenie dnia nad wodą. Transport uczestników na start trasy i odbiór z mety jest bezpłatny — wliczony w cenę spływu. Podwózka z dalszych obszarów wyceniana jest indywidualnie. Sprawdź cennik, wybierz trasę spływu i zarezerwuj kajaki online — do zobaczenia nad Pilicą!",
        ]}
        faq={[
          { q: "Ile kosztuje spływ kajakowy Pilicą?", a: "Ceny zaczynają się od 100 zł za kajak na popularnej trasie Spała–Zakościele. W cenie są kamizelki, wiosła, transport i darmowe ognisko po spływie. Gwarantujemy najniższą cenę w okolicy." },
          { q: "Czy trzeba umieć pływać, żeby popłynąć kajakiem?", a: "Nie jest to wymagane — każdy uczestnik dostaje kamizelkę asekuracyjną, a Pilica to spokojna, bezpieczna rzeka idealna dla początkujących i rodzin z dziećmi." },
          { q: "Czy zapewniacie transport na spływ?", a: "Tak. Transport uczestników na start trasy i odbiór z mety jest bezpłatny — wliczony w cenę spływu. Podwózka z dalszych obszarów (spoza Tomaszowa Mazowieckiego) jest wyceniana indywidualnie. Zadzwoń pod 787 028 770 po szczegóły." },
          { q: "Czy mogę zabrać psa na spływ?", a: "Tak, pies jest u nas mile widziany. Mamy kapoki (kamizelki) dla psów i polecamy spokojne trasy. Więcej na podstronie \"Z psem na kajak\"." },
        ]}
      />


      <Footer />
      <FloatingButtons />
    </div>
  );
}
